'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PageWhyPropTradingSchema } from '@/sanity/pages/PageWhyPropTradingTemplate';
import { GroqImage } from '@/helpers/Image';
import dotsGradientBg from '@/public/global/dots-gradient-bg.png';
import Image from 'next/image';

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HowIqTreatsSection({
  data,
}: {
  data: PageWhyPropTradingSchema;
}) {
  if (!data?.howIqTreatsTitle && !data?.howIqTreatsContent) return null;

  return (
    <section className="relative w-full flex flex-col items-center bg-white py-[8rem] lg:pt-[20rem] lg:pb-[20rem] px-[3rem] lg:px-[6rem]">
      <div className="absolute w-[calc(100%+50rem)] lg:w-full h-[48rem] top-0 left-1/2 -translate-x-1/2 mix-blend-exclusion pointer-events-none select-none">
        <Image fill alt="Background pattern" src={dotsGradientBg} />
      </div>
      <div className="w-[min(141.5rem,100%)] flex flex-col lg:flex-row items-stretch lg:items-center gap-[7rem] lg:gap-[12rem]">
        {/* Text side */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col gap-[4rem] flex-1"
        >
          <div className="flex flex-col gap-[2rem]">
            {data?.howIqTreatsChip && (
              <p className="mb-0 font-blacker italic text-[2.25rem] text-black/70 leading-[1.5]">
                {data.howIqTreatsChip}
              </p>
            )}
            {data?.howIqTreatsTitle && (
              <div className="richtext richtext-start prose-h2:text-[4rem] lg:prose-h2:text-[6rem] prose-h2:font-medium prose-h2:leading-[1.15] prose-h2:tracking-[-0.3rem]">
                <PortableText
                  value={data.howIqTreatsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.howIqTreatsContent && (
            <div className="richtext richtext-start prose-p:text-black/80 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:tracking-[-0.1125rem]">
              <PortableText
                value={data.howIqTreatsContent}
                components={richTextComponent}
              />
            </div>
          )}

          {(data?.howIqTreatsPersonName || data?.howIqTreatsPersonRole) && (
            <div className="flex flex-col gap-[4rem]">
              <div className="w-[54.75rem] max-w-full h-px bg-black/10" />
              <div className="flex flex-col gap-[0.5rem]">
                {data.howIqTreatsPersonName && (
                  <p className="mb-0 text-[2.75rem] lg:text-[3.5rem] font-medium leading-[1.12] tracking-[-0.14rem] text-black">
                    {data.howIqTreatsPersonName}
                  </p>
                )}
                {data.howIqTreatsPersonRole && (
                  <p className="mb-0 font-mono text-[1.75rem] text-[#228e71] uppercase tracking-[-0.0525rem] leading-normal">
                    {data.howIqTreatsPersonRole}
                  </p>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* Image side */}
        {data?.howIqTreatsImage && (
          <motion.div
            initial={{ opacity: 0, x: 24, scale: 0.98 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease, delay: 0.12 }}
            className="flex-shrink-0 w-full lg:w-[63.75rem] overflow-hidden"
          >
            <GroqImage
              src={data.howIqTreatsImage}
              alt={data.howIqTreatsImage.name || ''}
              className="w-full h-auto object-cover -scale-y-100 rotate-180"
              style={{ width: '100%', height: 'auto' }}
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
